import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import type { RootState } from './index';
import { setSession, updateUser, logout } from './authSlice';
import {
  setOrganizations,
  addOrganization,
  removeOrganization,
  setCurrentOrgId,
  setCurrentOrg,
  clearCurrentOrg,
  reset as resetOrg,
} from './organizationSlice';
import { reset as resetVault } from './vaultSlice';
import { setMode, toggleTheme } from './themeSlice';
import { clearEntitlements } from './subscriptionSlice';

export const persistMiddleware = createListenerMiddleware();

// Auth → localStorage
persistMiddleware.startListening({
  matcher: isAnyOf(setSession, updateUser),
  effect: (_action, listenerApi) => {
    const { auth } = listenerApi.getState() as RootState;
    try {
      localStorage.setItem(
        'ssp-enterprise-auth',
        JSON.stringify({
          session: auth.session,
          user: auth.user,
          isAuthenticated: auth.isAuthenticated,
        })
      );
    } catch {
      // Ignore quota errors
    }
  },
});

// Logout → clear all keys and reset org + vault + subscription state.
// Without clearing entitlements here, the next user on a shared device
// would see the previous user's paid-tier affordances flash until the
// first /entitlements fetch lands.
persistMiddleware.startListening({
  actionCreator: logout,
  effect: (_action, listenerApi) => {
    localStorage.removeItem('ssp-enterprise-auth');
    localStorage.removeItem('ssp-enterprise-current-org');
    // WalletConnect vault bindings belong to this user: the next user on a
    // shared browser must not inherit live dApp sessions bound to these vaults.
    // Sessions left without a binding are disconnected on the next WalletKit
    // init (WalletConnectContext).
    localStorage.removeItem('ssp_wc_bindings');
    listenerApi.dispatch(resetOrg());
    listenerApi.dispatch(resetVault());
    listenerApi.dispatch(clearEntitlements());
  },
});

// Organization → localStorage
persistMiddleware.startListening({
  matcher: isAnyOf(
    setOrganizations,
    addOrganization,
    removeOrganization,
    setCurrentOrgId,
    setCurrentOrg,
    clearCurrentOrg,
    resetOrg
  ),
  effect: (_action, listenerApi) => {
    const { organization } = listenerApi.getState() as RootState;
    try {
      if (organization.currentOrgId) {
        localStorage.setItem('ssp-enterprise-current-org', organization.currentOrgId);
      } else {
        localStorage.removeItem('ssp-enterprise-current-org');
      }
    } catch {
      // Ignore quota errors
    }
  },
});

// Theme → localStorage
persistMiddleware.startListening({
  matcher: isAnyOf(setMode, toggleTheme),
  effect: (_action, listenerApi) => {
    const { theme } = listenerApi.getState() as RootState;
    try {
      localStorage.setItem('ssp-enterprise-theme', JSON.stringify({ mode: theme.mode }));
    } catch {
      // Ignore quota errors
    }
  },
});

/**
 * Set up cross-tab sync via storage events.
 * Call once on app mount. Returns cleanup function.
 */
export function setupCrossTabSync(
  dispatch: (action: unknown) => void,
  getState: () => RootState
): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key === 'ssp-enterprise-auth') {
      if (!e.newValue) {
        // Another tab logged out
        const { auth } = getState();
        if (auth.isAuthenticated) {
          dispatch(logout());
        }
      }
    }

    if (e.key === 'ssp-enterprise-current-org' && e.newValue) {
      const { organization } = getState();
      if (organization.currentOrgId !== e.newValue) {
        dispatch(setCurrentOrgId(e.newValue));
      }
    }

    if (e.key === 'ssp-enterprise-theme' && e.newValue) {
      try {
        const parsed = JSON.parse(e.newValue) as { mode: 'light' | 'dark' | 'system' };
        const { theme } = getState();
        if (theme.mode !== parsed.mode) {
          dispatch(setMode(parsed.mode));
        }
      } catch {
        // Ignore parse errors
      }
    }
  };

  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener('storage', handler);
  };
}
